import React, { Component } from 'react';
import { View } from 'react-native';

import { ViewPropTypes } from 'deprecated-react-native-prop-types';
import {
  isFunction as _isFunction,
  map as _map,
  get as _get,
  remove as _remove,
  cloneDeep as _cloneDeep,
} from 'lodash';
import PropTypes from 'prop-types';

import FoundationTestKeys from '../../FoundationTestKeys';
import { LightThemeColors } from '../Themes';

import Accordion from './Accordion/Accordion';
import StepBody from './StepBody/StepBody';
import StepHeader from './StepHeader/StepHeader';
import styles from './Timeline.Styles';
import StepStatuses from './Utils/StepStatuses';
import { TimelineEvents } from './Utils/TimelineEvents';

class TimeLine extends Component {
  constructor(props) {
    super(props);
    const { initialActiveSections, steps } = props;

    const stepsWithStatus = this.transformSteps(steps);

    this.state = {
      activeSections: initialActiveSections,
      steps: stepsWithStatus,
    };

    this.initialRender = true;
  }

  onNextStep = (currentStep, currentActiveSectionIndex) => () => {
    const newActiveSections = this.updateActiveSections(
      currentActiveSectionIndex,
      currentActiveSectionIndex + 1,
    );
    const updatedSteps = this.updateStep(
      currentStep,
      currentActiveSectionIndex,
      StepStatuses.COMPLETED,
    );

    this.setState({ activeSections: newActiveSections, steps: updatedSteps });
  };

  onPreviousStep = (currentStep, currentActiveSectionIndex) => () => {
    const newActiveSections = this.updateActiveSections(
      currentActiveSectionIndex,
      currentActiveSectionIndex - 1,
    );
    const updatedSteps = this.updateStep(currentStep, currentActiveSectionIndex, StepStatuses.NEW);

    this.setState({ activeSections: newActiveSections, steps: updatedSteps });
  };

  onSkipStep = (currentStep, currentActiveSectionIndex) => () => {
    const newActiveSections = this.updateActiveSections(
      currentActiveSectionIndex,
      currentActiveSectionIndex + 1,
    );
    const updatedSteps = this.updateStep(
      currentStep,
      currentActiveSectionIndex,
      StepStatuses.SKIPPED,
    );

    this.setState({ activeSections: newActiveSections, steps: updatedSteps });
  };

  onAnimationEnd = (section, index, isActive) => {
    const { getActiveSectionYPosition } = this.props;

    TimelineEvents.emit(this.currentStepYPosition);

    if (isActive && _isFunction(getActiveSectionYPosition)) {
      getActiveSectionYPosition(this.currentStepYPosition);
    }
  };

  onStepLayout = ({ nativeEvent: { layout } }, step, index, isActive) => {
    if (isActive) {
      this.currentStepYPosition = layout.y;

      TimelineEvents.emit(this.currentStepYPosition);

      if (this.initialRender) {
        const { getActiveSectionYPosition } = this.props;

        if (_isFunction(getActiveSectionYPosition)) {
          getActiveSectionYPosition(this.currentStepYPosition);
        }

        this.initialRender = false;
      }
    }
  };

  getSectionContainerStyle = (section, index, isActive) => {
    const { steps } = this.state;
    const currentStepStatus = _get(steps[index], 'status');

    const isCompleted = currentStepStatus === StepStatuses.COMPLETED;
    const isFinalStep = steps.length - 1 === index;

    let style = styles.newOrSkippedSectionContainerStyle(this.props.theme);

    if (isFinalStep) {
      style = styles.finalSectionContainerStyle;
    } else if (isActive) {
      style = styles.activeSectionContainerStyle(this.props.theme);
    } else if (isCompleted) {
      style = styles.completedSectionContainerStyle(this.props.theme);
    }

    return style;
  };

  transformSteps = steps =>
    _map(steps, step => ({
      ...step,
      status: step.status || StepStatuses.NEW,
    }));

  updateSteps = steps => {
    this.setState({ steps: this.transformSteps(steps) });
  };

  startTimeLine = () => {
    const { onStart } = this.props;
    this.setState({ activeSections: [0] });

    if (_isFunction(onStart)) {
      onStart();
    }
  };

  updateStep(step, index, status) {
    const { steps } = this.state;
    const { onStepUpdate } = this.props;
    const updatedSteps = _map(steps, (currentStep, i) => {
      if (i !== index) {
        return currentStep;
      }

      return {
        ...currentStep,
        status: status,
      };
    });

    if (_isFunction(onStepUpdate)) {
      onStepUpdate(step, index, status);
    }

    return updatedSteps;
  }

  updateActiveSections = (currentActiveSectionIndex, newActiveSectionIndex) => {
    const { steps, activeSections } = this.state;
    const { onTimeLineFinish } = this.props;

    const newActiveSections = _cloneDeep(activeSections);
    _remove(newActiveSections, activeSection => activeSection === currentActiveSectionIndex);

    if (newActiveSectionIndex === steps.length && _isFunction(onTimeLineFinish)) {
      onTimeLineFinish();
    } else {
      newActiveSections.push(newActiveSectionIndex);
    }

    return newActiveSections;
  };

  renderSectionTitle = (section, index, isActive) => {
    const { renderSectionTitle } = this.props;

    if (_isFunction(renderSectionTitle)) {
      return renderSectionTitle(section, index, isActive);
    }
  };

  renderHeader = (step, index, isActive, steps) => {
    const {
      renderStepTitle,
      stepTitlePropName,
      accessibilityLabelPropName,
      stepTitleContainerStyle,
      theme,
    } = this.props;

    const isNew = step.status === StepStatuses.NEW;
    const isSkipped = step.status === StepStatuses.SKIPPED;
    const isCompleted = step.status === StepStatuses.COMPLETED;
    const isFinalStep = steps.length - 1 === index;

    return (
      <StepHeader
        step={step}
        titlePropName={stepTitlePropName}
        accessibilityLabelPropName={accessibilityLabelPropName}
        renderTitle={renderStepTitle}
        titleContainerStyle={stepTitleContainerStyle}
        index={index}
        isActive={isActive}
        isNew={isNew}
        isSkipped={isSkipped}
        isCompleted={isCompleted}
        isFinalStep={isFinalStep}
        steps={steps}
        theme={theme}
      />
    );
  };

  renderItem = (step, index, isActive, steps) => {
    const { renderStepBody, stepBodyProps, stepsImages, theme } = this.props;

    const isFinalStep = steps.length - 1 === index;

    return (
      <StepBody
        step={step}
        isActive={isActive}
        isFinalStep={isFinalStep}
        index={index}
        steps={steps}
        onNextStep={this.onNextStep(step, index)}
        onSkipStep={this.onSkipStep(step, index)}
        onPreviousStep={this.onPreviousStep(step, index)}
        additionalProps={stepBodyProps}
        renderStepBody={renderStepBody}
        stepsImages={stepsImages}
        theme={theme}
      />
    );
  };

  render() {
    const { steps, activeSections } = this.state;
    const { containerStyle, ...props } = this.props;

    return (
      <View
        style={[styles.scrollViewContainer, containerStyle]}
        testID={FoundationTestKeys.timelineView}
      >
        <Accordion
          onChange={() => {}}
          touchableProps={{ underlayColor: 'transparent' }}
          containerStyle={styles.accordionStyle}
          {...props}
          sections={steps}
          activeSections={activeSections}
          renderSectionTitle={this.renderSectionTitle}
          renderHeader={this.renderHeader}
          renderContent={this.renderItem}
          renderSectionContainerStyle={this.getSectionContainerStyle}
          onAnimationEnd={this.onAnimationEnd}
          onSectionContainerLayout={this.onStepLayout}
        />
      </View>
    );
  }
}

TimeLine.defaultProps = {
  initialActiveSections: [],
  navigation: {},
  actions: {},
  checkedSteps: [],
  renderSectionTitle: () => null,
  renderStepBody: () => null,
  stepTitlePropName: 'title',
  accessibilityLabelPropName: FoundationTestKeys.accessibilityLabelPropName,
  getActiveSectionYPosition: () => {},
  stepBodyProps: {},
  theme: LightThemeColors,
};

TimeLine.propTypes = {
  steps: PropTypes.array.isRequired,
  onStart: PropTypes.func,
  onStepUpdate: PropTypes.func,
  onTimeLineFinish: PropTypes.func,
  initialActiveSections: PropTypes.arrayOf(PropTypes.number).isRequired,
  renderSectionTitle: PropTypes.func,
  renderStepBody: PropTypes.func.isRequired,
  renderStepTitle: PropTypes.func,
  stepTitlePropName: PropTypes.string,
  stepId: PropTypes.string,
  stepTitleContainerStyle: ViewPropTypes.style,
  containerStyle: ViewPropTypes.style,
  getActiveSectionYPosition: PropTypes.func,
  accessibilityLabelPropName: PropTypes.string,
  stepBodyProps: PropTypes.object,
  stepsImages: PropTypes.object,
  theme: PropTypes.shape({
    name: PropTypes.string,
    isDark: PropTypes.bool,
    colors: PropTypes.object,
  }),
};

export default TimeLine;
